import * as Popover from "@radix-ui/react-popover";
import { Info } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

export type FieldInfoPopoverProps = {
  /** Read aloud in place of the icon — name the field it belongs to. */
  ariaLabel: string;
  children: ReactNode;
};

/**
 * Small "i" next to a field label that reveals the requirement behind it.
 *
 * Opens on hover, as the secretariat asked for, but is a real button on a
 * Popover rather than a tooltip: the texts it carries run several paragraphs, so
 * they have to survive a tap on touch, an Enter from the keyboard, and the
 * pointer travelling from the icon into the text itself — none of which a
 * hover-only tooltip does.
 */
const FieldInfoPopover = ({ ariaLabel, children }: FieldInfoPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => cancelClose, []);

  const handleOpen = () => {
    cancelClose();
    setIsOpen(true);
  };

  // Grace period so the pointer can cross the gap between icon and content
  // without the popover closing under it.
  const handleCloseSoon = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setIsOpen(false), 150);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    cancelClose();
    setIsOpen(nextOpen);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="cle-field-info__trigger"
          onMouseEnter={handleOpen}
          onMouseLeave={handleCloseSoon}
        >
          <Info size={15} aria-hidden strokeWidth={2} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          side="top"
          sideOffset={6}
          collisionPadding={16}
          className="cle-field-info__content"
          onMouseEnter={cancelClose}
          onMouseLeave={handleCloseSoon}
          // Hover-opening must not steal focus from whatever the member is doing.
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {children}
          <Popover.Arrow className="cle-field-info__arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default FieldInfoPopover;
